// src/pages/Accounting/Reports/AccountList/components/CustomizeDrawer.tsx
// Right-side drawer with Data + Visual tabs for customizing the Account List report.

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { ReportCustomization } from '../types';
import CustomizeDataTab from './CustomizeDataTab';
import CustomizeVisualTab from './CustomizeVisualTab';

interface CustomizeDrawerProps {
  open: boolean;
  onClose: () => void;
  draft: ReportCustomization;
  updateDraft: (patch: Partial<ReportCustomization>) => void;
  onApply: () => void;
  onResetToDefault: () => void;
  onCancel: () => void;
}

type TabKey = 'data' | 'visual';

export default function CustomizeDrawer({ open, onClose, draft, updateDraft, onApply, onResetToDefault, onCancel }: CustomizeDrawerProps) {
  const [tab, setTab] = useState<TabKey>('data');

  if (!open) return null;

  const handleCancel = () => { onCancel(); onClose(); };
  const handleApply = () => { onApply(); onClose(); };

  return createPortal(
    <div onClick={handleCancel} className="fixed inset-0 z-[1000] bg-black/30 flex justify-end">
      <div onClick={(e) => e.stopPropagation()} className="bg-white border-l border-line shadow-2xl w-full max-w-[440px] h-full flex flex-col">
        <div className="relative px-5 py-4 border-b border-lineSoft">
          <h4 className="text-[15px] font-extrabold text-ink text-center m-0">Customize</h4>
          <button type="button" onClick={handleCancel} aria-label="Close" className="absolute right-4 top-4 text-ink3 cursor-pointer border-none bg-transparent hover:text-ink">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 pt-4">
          <div className="grid grid-cols-2 bg-panel border border-lineSoft rounded-md p-[3px]">
            <button
              type="button"
              onClick={() => setTab('data')}
              className={`py-1.5 px-2.5 rounded text-[12px] font-semibold border-none cursor-pointer ${
                tab === 'data' ? 'bg-white text-ink shadow-[0_1px_3px_rgba(14,26,26,0.06)]' : 'bg-transparent text-ink3'
              }`}
            >
              Data
            </button>
            <button
              type="button"
              onClick={() => setTab('visual')}
              className={`py-1.5 px-2.5 rounded text-[12px] font-semibold border-none cursor-pointer ${
                tab === 'visual' ? 'bg-white text-ink shadow-[0_1px_3px_rgba(14,26,26,0.06)]' : 'bg-transparent text-ink3'
              }`}
            >
              Visual
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {tab === 'data'
            ? <CustomizeDataTab draft={draft} updateDraft={updateDraft} />
            : <CustomizeVisualTab draft={draft} updateDraft={updateDraft} />
          }
        </div>

        <div className="flex justify-between items-center gap-2 px-5 py-3.5 border-t border-lineSoft">
          <button type="button" onClick={onResetToDefault} className="text-ink3 text-[11.5px] font-bold border-none bg-transparent cursor-pointer hover:text-ink">
            Reset to default
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={handleCancel} className="text-ink3 text-[12px] font-bold px-3 py-1.5 border-none bg-transparent cursor-pointer hover:text-ink">
              Cancel
            </button>
            <button type="button" onClick={handleApply} className="bg-teal-dark text-white text-[12px] font-bold px-3.5 py-1.5 rounded-md border-none cursor-pointer hover:brightness-110">
              Apply changes
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}